import { Badge } from "@/components/ui/badge";
import { supabase } from "@/lib/supabase";
import {
  createWorkspaceInvitation,
  getWorkspaceInvitationStatus,
  hasExistingInvitation,
  subscribeToUserNotifications,
  unsubscribeFromNotifications,
} from "@/lib/utils/notificationUtils";
import { updateUserWorkspacesOnRemoval } from "@/lib/utils/workspaceCollaboration";
import { ChatCompletionService } from "@/services/openai/chatCompletionService";
import useAuthStore from "@/store/authStore";
import { Crown, Shield } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const PAGE_SIZE = 20;

// Create service instance
const chatCompletionService = new ChatCompletionService();

export function useWorkspaceSettings({
  workspace,
  isOpen,
  user,
  onSaveChanges,
  onDeleteWorkspace,
}) {
  const navigate = useNavigate();

  // ===== STATE DECLARATIONS =====

  // Form state
  const [editForm, setEditForm] = useState({
    name: "",
    description: "",
    instructions: "",
  });

  const [workspaceSettings, setWorkspaceSettings] = useState({
    notifications: false,
    private: false,
    allowMemberInvites: false,
    include_workspace_instructions: true,
  });

  const [logoData, setLogoData] = useState(null);

  // UI state
  const [activeMainTab, setActiveMainTab] = useState("details");
  const [activeTeamTab, setActiveTeamTab] = useState("invite");

  // Team management state
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [inviteMessage, setInviteMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [members, setMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [sentInvitations, setSentInvitations] = useState([]);
  const [recentlySentInvites, setRecentlySentInvites] = useState([]);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMoreResults, setHasMoreResults] = useState(true);
  const [loadingMoreUsers, setLoadingMoreUsers] = useState(false);
  const [realtimeChannel, setRealtimeChannel] = useState(null);
  const resultsContainerRef = useRef(null);

  // Role management state
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [isOwner, setIsOwner] = useState(false);

  // Transfer ownership state
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [selectedNewOwner, setSelectedNewOwner] = useState(null);
  const [isTransferring, setIsTransferring] = useState(false);

  // Auth store
  const {
    workspaces,
    setWorkspaces,
    getWorkspaceMembers,
    updateMemberRole,
    removeWorkspaceMember,
    getUserWorkspaceRole,
    isWorkspaceOwner,
  } = useAuthStore();

  // ===== UTILITY FUNCTIONS =====

  const getInitials = (name) => {
    if (!name) return "??";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  const getAvatarUrl = (user) => {
    if (!user) return null;

    // Try multiple avatar sources
    if (user?.avatar_url) return user.avatar_url;
    if (user?.profile?.avatar_url) return user.profile.avatar_url;
    if (user?.settings?.profile?.avatar_url)
      return user.settings.profile.avatar_url;

    return null;
  };

  const getUserDisplayName = (user) => {
    if (!user) return "Unnamed User";

    // Handle different data structures - sometimes it's nested in settings, sometimes direct
    const settingsDisplayName = user?.settings?.profile?.display_name;
    const directDisplayName = user?.display_name;
    const profileDisplayName = user?.profile?.display_name;

    // Try all possible display name sources
    const displayName =
      directDisplayName || settingsDisplayName || profileDisplayName;

    if (displayName) {
      if (user.full_name && displayName !== user.full_name) {
        return `${displayName} (${user.full_name})`;
      }
      return displayName;
    }

    return user.full_name || user.email || "Unnamed User";
  };

  const getRoleBadge = (role) => {
    if (role === "owner") {
      return (
        <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white border-0 text-[10px] px-2 py-0.5 flex items-center gap-1">
          <Crown className="h-3 w-3" />
          Owner
        </Badge>
      );
    }
    return (
      <Badge className="bg-gradient-to-r from-blue-500 to-purple-600 text-white border-0 text-[10px] px-2 py-0.5 flex items-center gap-1">
        <Shield className="h-3 w-3" />
        Member
      </Badge>
    );
  };

  // Function to refresh workspace status
  const refreshWorkspaceStatus = async () => {
    if (!workspace?.id) return;

    try {
      const status = await getWorkspaceInvitationStatus(workspace.id);
      setMembers(status.members);
      setPendingInvites(status.pendingInvites);

      // Also refresh sent invitations
      if (user?.id) {
        const { data: sentData, error: sentError } = await supabase
          .from("notifications")
          .select("*")
          .eq("sender_id", user.id)
          .eq("type", "workspace_invitation")
          .eq("workspace_id", workspace.id)
          .order("created_at", { ascending: false });

        if (!sentError) {
          // Process sent invitations to include recipient profiles
          const processedSent = await Promise.all(
            (sentData || []).map(async (notification) => {
              let receiverProfile = null;

              if (notification.receiver_id) {
                try {
                  const { data: profileData, error } = await supabase
                    .from("profiles")
                    .select("*")
                    .eq("id", notification.receiver_id)
                    .single();

                  if (!error) {
                    receiverProfile = profileData;
                  }
                } catch (err) {
                  console.error("Exception in receiver profile fetch:", err);
                }
              }

              return {
                ...notification,
                direction: "sent",
                receiver_display_name:
                  receiverProfile?.full_name ||
                  receiverProfile?.username ||
                  "Unknown User",
                receiver_avatar_url: receiverProfile?.avatar_url || null,
                profiles: receiverProfile,
              };
            })
          );

          setSentInvitations(processedSent);
        }
      }
    } catch (error) {
      // Error refreshing workspace status
    }
  };

  // ===== EFFECT HOOKS =====

  // Update form when workspace changes or dialog opens
  useEffect(() => {
    if (workspace && isOpen) {
      setEditForm({
        name: workspace.name || "",
        description: workspace.description || "",
        instructions: workspace.instructions || "",
      });
      setWorkspaceSettings({
        notifications: workspace.settings?.notifications || false,
        private: workspace.settings?.private || false,
        allowMemberInvites: workspace.settings?.allowMemberInvites || false,
        include_workspace_instructions:
          workspace.include_workspace_instructions || true,
      });
      // Reset logo data when workspace changes
      setLogoData(null);

      // Set default invitation message for team workspaces
      if (workspace.is_team && user) {
        setInviteMessage(
          `Join me in collaborating on "${workspace.name}" - ${user.full_name || user.email}`
        );
      }

      // Set default tab based on workspace type
      setActiveMainTab("details");
    }
  }, [workspace, isOpen, user]);

  // Set up realtime subscription for team workspaces
  useEffect(() => {
    let channel = null;

    const setupRealtimeSubscription = async () => {
      if (!workspace?.id || !isOpen || !user?.id || !workspace?.is_team) return;

      try {
        channel = await subscribeToUserNotifications(
          user.id,
          (payload) => {
            if (
              payload.new &&
              payload.new.type === "workspace_invitation" &&
              payload.new.workspace_id === workspace.id
            ) {
              refreshWorkspaceStatus();
            }
          },
          (payload) => {
            if (
              payload.new &&
              payload.new.type === "workspace_invitation" &&
              payload.new.workspace_id === workspace.id &&
              payload.new.status !== payload.old.status
            ) {
              refreshWorkspaceStatus();
            }
          }
        );

        setRealtimeChannel(channel);
      } catch (error) {
        // Error setting up realtime subscription
      }
    };

    setupRealtimeSubscription();

    return () => {
      if (channel) {
        unsubscribeFromNotifications(channel);
        setRealtimeChannel(null);
      }
    };
  }, [isOpen, workspace?.id, workspace?.is_team, user?.id]);

  // Load workspace members with roles and check user permissions
  useEffect(() => {
    const loadWorkspaceData = async () => {
      if (!workspace?.id || !workspace?.is_team || !user?.id) return;

      setIsLoadingMembers(true);
      try {
        // Get workspace members with roles
        const membersData = await getWorkspaceMembers(workspace.id);

        // Fetch profile data for each member
        if (membersData && membersData.length > 0) {
          const membersWithProfiles = await Promise.all(
            membersData.map(async (member) => {
              try {
                const { data: profileData, error } = await supabase
                  .from("profiles")
                  .select("*")
                  .eq("id", member.user_id)
                  .single();

                if (!error && profileData) {
                  return {
                    ...member,
                    ...profileData, // Merge profile data with member data
                    profile: profileData, // Also keep it in profile field for compatibility
                  };
                }
                return member;
              } catch (err) {
                console.error(
                  "Error fetching profile for member:",
                  member.user_id,
                  err
                );
                return member;
              }
            })
          );
          setWorkspaceMembers(membersWithProfiles);
        } else {
          setWorkspaceMembers([]);
        }

        // Get current user's role and check if owner
        const role = await getUserWorkspaceRole(workspace.id, user.id);
        const ownerStatus = await isWorkspaceOwner(workspace.id, user.id);
        setUserRole(role);
        setIsOwner(ownerStatus);
      } catch (error) {
        console.error("Error loading workspace data:", error);
      } finally {
        setIsLoadingMembers(false);
      }
    };

    if (isOpen) {
      loadWorkspaceData();
    }
  }, [
    isOpen,
    workspace?.id,
    workspace?.is_team,
    user?.id,
    getWorkspaceMembers,
    getUserWorkspaceRole,
    isWorkspaceOwner,
  ]);

  // Load workspace members and pending invites for team workspaces
  useEffect(() => {
    const loadWorkspaceStatus = async () => {
      if (!workspace?.id || !workspace?.is_team) return;

      setIsLoadingStatus(true);
      try {
        const status = await getWorkspaceInvitationStatus(workspace.id);
        setMembers(status.members);
        setPendingInvites(status.pendingInvites);

        // Also fetch sent invitations for this workspace
        if (user?.id) {
          const { data: sentData, error: sentError } = await supabase
            .from("notifications")
            .select("*")
            .eq("sender_id", user.id)
            .eq("type", "workspace_invitation")
            .eq("workspace_id", workspace.id)
            .order("created_at", { ascending: false });

          if (sentError) {
            console.error("Error fetching sent invitations:", sentError);
          } else {
            // Process sent invitations to include recipient profiles
            const processedSent = await Promise.all(
              (sentData || []).map(async (notification) => {
                let receiverProfile = null;

                if (notification.receiver_id) {
                  try {
                    const { data: profileData, error } = await supabase
                      .from("profiles")
                      .select("*")
                      .eq("id", notification.receiver_id)
                      .single();

                    if (!error) {
                      receiverProfile = profileData;
                    }
                  } catch (err) {
                    console.error("Exception in receiver profile fetch:", err);
                  }
                }

                return {
                  ...notification,
                  direction: "sent",
                  receiver_display_name:
                    receiverProfile?.full_name ||
                    receiverProfile?.username ||
                    "Unknown User",
                  receiver_avatar_url: receiverProfile?.avatar_url || null,
                  profiles: receiverProfile, // For consistency with pending invites structure
                };
              })
            );

            setSentInvitations(processedSent);
          }
        }
      } catch (error) {
        // Error loading workspace status
      } finally {
        setIsLoadingStatus(false);
      }
    };

    if (isOpen) {
      loadWorkspaceStatus();
    }
  }, [isOpen, workspace?.id, workspace?.is_team, user?.id]);

  // Reset team management state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setSearchResults([]);
      setSelectedUsers([]);
      setCurrentPage(0);
      setHasMoreResults(true);
      setSentInvitations([]);
      setRecentlySentInvites([]);
      setShowSuccessNotification(false);
      setSuccessMessage("");
      setActiveMainTab("details");
    }
  }, [isOpen]);

  // ===== HANDLER FUNCTIONS =====

  // Basic settings handlers
  const handleSettingChange = async (setting, value) => {
    setWorkspaceSettings((prev) => ({ ...prev, [setting]: value }));
  };

  const handleLogoChange = (data) => {
    setLogoData(data);
  };

  const handleSubmit = async () => {
    // Log the data being saved

    // Explicitly extract the image path to ensure it's correctly passed
    const imagePathToSave = logoData?.image_path || workspace?.image_path;

    // Ensure image_path is properly handled by creating flat object with correct field name
    const updatedWorkspace = {
      ...editForm,
      settings: workspaceSettings,
      image_path: imagePathToSave, // Make sure we use snake_case for DB field
      include_workspace_instructions:
        workspaceSettings.include_workspace_instructions,
    };

    // Call the update function with the prepared data
    await onSaveChanges(updatedWorkspace);
  };

  const handleDeleteWorkspace = async () => {
    try {
      // Prevent deleting the last workspace
      if (workspaces.length <= 1) {
        console.error("Cannot delete the last project");
        return;
      }

      // Confirm deletion
      if (
        !confirm(
          "Are you sure you want to delete this project? This action cannot be undone."
        )
      ) {
        return;
      }

      // First, delete the associated vector store if it exists
      if (workspace.vector_store_id) {
        try {
          // Delete the vector store using OpenAI client
          await chatCompletionService.deleteVectorStore(
            workspace.vector_store_id
          );
        } catch (vectorStoreError) {
          console.error(
            "Error deleting OpenAI vector store:",
            vectorStoreError
          );
          // Continue with workspace deletion despite vector store deletion error
        }
      }

      // Call the original onDeleteWorkspace function passed as prop
      await onDeleteWorkspace();
    } catch (error) {
      console.error("Error deleting workspace:", error);
    }
  };

  // Team management functions
  const handleUserSearch = async () => {
    if (!searchQuery.trim()) return;

    try {
      setIsSearching(true);
      setSearchResults([]);
      setCurrentPage(0);
      setHasMoreResults(true);

      const { data: nameResults, error: nameError } = await supabase
        .from("profiles")
        .select("id, full_name, email, avatar_url, settings")
        .ilike("full_name", `%${searchQuery}%`)
        .neq("id", user.id)
        .range(0, PAGE_SIZE - 1)
        .order("full_name", { ascending: true });

      if (nameError) throw nameError;

      const { data: emailResults, error: emailError } = await supabase
        .from("profiles")
        .select("id, full_name, email, avatar_url, settings")
        .ilike("email", `%${searchQuery}%`)
        .neq("id", user.id)
        .range(0, PAGE_SIZE - 1)
        .order("full_name", { ascending: true });

      const combinedResults = [...(nameResults || [])];
      if (emailResults) {
        emailResults.forEach((emailUser) => {
          if (!combinedResults.some((user) => user.id === emailUser.id)) {
            combinedResults.push(emailUser);
          }
        });
      }

      const processedResults = combinedResults.map((user) => ({
        ...user,
        display_name: user.settings?.profile?.display_name || "",
      }));

      processedResults.sort((a, b) => {
        const nameA = a.full_name || a.display_name || a.email || "";
        const nameB = b.full_name || b.display_name || b.email || "";
        return nameA.localeCompare(nameB);
      });

      const paginatedResults = processedResults.slice(0, PAGE_SIZE);
      setSearchResults(paginatedResults);
      setHasMoreResults(combinedResults.length > PAGE_SIZE);
    } catch (error) {
      // Error searching users
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUsers((prev) => [...prev, user]);
    setSearchResults((prev) => prev.filter((result) => result.id !== user.id));
  };

  const handleRemoveUser = (userId) => {
    setSelectedUsers((prev) => prev.filter((user) => user.id !== userId));
  };

  const handleSendInvitations = async () => {
    if (!workspace || !user || selectedUsers.length === 0) return;

    setIsSending(true);
    try {
      if (!user.id) {
        throw new Error("You must be logged in to send invitations.");
      }

      const senderUserId = user.id;

      // Check workspace ownership and membership
      const { data: workspace_data, error: workspace_error } = await supabase
        .from("workspaces")
        .select("workspace_members, owner_id")
        .eq("id", workspace.id)
        .single();

      if (workspace_error) {
        throw new Error(
          "Unable to verify workspace details: " + workspace_error.message
        );
      }

      const isOwner = workspace_data.owner_id === senderUserId;
      const isMember =
        workspace_data.workspace_members &&
        workspace_data.workspace_members.includes(senderUserId);

      if (!isOwner && !isMember) {
        throw new Error(
          "You must be a workspace member or owner to send invitations"
        );
      }

      const successfulInvites = [];
      const failedInvites = [];

      for (const recipient of selectedUsers) {
        try {
          const alreadyInvited = await hasExistingInvitation(
            recipient.id,
            workspace.id
          );

          if (alreadyInvited) {
            failedInvites.push({
              user: recipient,
              reason: "User already has a pending invitation",
            });
            continue;
          }

          const safeWorkspaceData = {
            id: String(workspace.id),
            name: workspace.name ? String(workspace.name) : "",
            team_key: workspace.team_key ? String(workspace.team_key) : "",
            inviteMessage: inviteMessage ? String(inviteMessage) : "",
          };

          const safeRecipientId = String(recipient.id);
          const safeSenderId = String(senderUserId);

          await createWorkspaceInvitation(
            safeRecipientId,
            safeSenderId,
            safeWorkspaceData
          );
          successfulInvites.push(recipient);
        } catch (error) {
          failedInvites.push({
            user: recipient,
            reason: error.message || "Unknown error",
          });
        }
      }

      // Handle results and show appropriate feedback
      if (successfulInvites.length > 0) {
        // Track recently sent invites for enhanced notification display
        setRecentlySentInvites(successfulInvites.map((user) => user.id));

        // Show success notification
        let message;
        if (failedInvites.length > 0) {
          message = `${successfulInvites.length} invitation(s) sent successfully, ${failedInvites.length} failed.`;
        } else {
          message =
            successfulInvites.length === 1
              ? `Invitation sent to ${getUserDisplayName(successfulInvites[0])}!`
              : `${successfulInvites.length} invitations sent successfully!`;
        }

        setSuccessMessage(message);
        setShowSuccessNotification(true);

        setSelectedUsers([]);
        setActiveTeamTab("pending");
        // Refresh workspace status to show the new sent invitations
        refreshWorkspaceStatus();

        // Clear the "recently sent" indicator after 10 seconds
        setTimeout(() => {
          setRecentlySentInvites([]);
        }, 10000);

        // Hide success notification after 5 seconds
        setTimeout(() => {
          setShowSuccessNotification(false);
        }, 5000);
      } else if (failedInvites.length > 0) {
        // All invitations failed
        const message = `Failed to send invitations: ${failedInvites[0].reason}`;
        setSuccessMessage(message);
        setShowSuccessNotification(true);

        // Hide error notification after 7 seconds
        setTimeout(() => {
          setShowSuccessNotification(false);
        }, 7000);
      }
    } catch (error) {
      console.error("Error sending invitations:", error);
      setSuccessMessage(`Failed to send invitations: ${error.message}`);
      setShowSuccessNotification(true);

      // Hide error notification after 7 seconds
      setTimeout(() => {
        setShowSuccessNotification(false);
      }, 7000);
    } finally {
      setIsSending(false);
    }
  };

  // Role management functions
  const handleRoleChange = async (memberId, newRole) => {
    if (!workspace?.id || !isOwner) return;

    try {
      await updateMemberRole(workspace.id, memberId, newRole);

      // Refresh workspace members with profiles
      const membersData = await getWorkspaceMembers(workspace.id);
      if (membersData && membersData.length > 0) {
        const membersWithProfiles = await Promise.all(
          membersData.map(async (member) => {
            try {
              const { data: profileData, error } = await supabase
                .from("profiles")
                .select("*")
                .eq("id", member.user_id)
                .single();

              if (!error && profileData) {
                return {
                  ...member,
                  ...profileData,
                  profile: profileData,
                };
              }
              return member;
            } catch (err) {
              console.error(
                "Error fetching profile for member:",
                member.user_id,
                err
              );
              return member;
            }
          })
        );
        setWorkspaceMembers(membersWithProfiles);
      }

      // Show success message
      setSuccessMessage(`Role updated successfully!`);
      setShowSuccessNotification(true);
      setTimeout(() => setShowSuccessNotification(false), 3000);
    } catch (error) {
      console.error("Error updating member role:", error);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!workspace?.id || !isOwner) return;

    if (
      !confirm(
        "Are you sure you want to remove this member from the workspace?"
      )
    ) {
      return;
    }

    try {
      await removeWorkspaceMember(workspace.id, memberId);

      // Update the user's profile to remove workspace from user_workspaces
      const updateSuccess = await updateUserWorkspacesOnRemoval(
        memberId,
        workspace.id
      );
      if (!updateSuccess) {
        console.warn(
          "Failed to update user workspaces in profile, but member was removed from workspace"
        );
      }

      // Refresh workspace members with profiles
      const membersData = await getWorkspaceMembers(workspace.id);
      if (membersData && membersData.length > 0) {
        const membersWithProfiles = await Promise.all(
          membersData.map(async (member) => {
            try {
              const { data: profileData, error } = await supabase
                .from("profiles")
                .select("*")
                .eq("id", member.user_id)
                .single();

              if (!error && profileData) {
                return {
                  ...member,
                  ...profileData,
                  profile: profileData,
                };
              }
              return member;
            } catch (err) {
              console.error(
                "Error fetching profile for member:",
                member.user_id,
                err
              );
              return member;
            }
          })
        );
        setWorkspaceMembers(membersWithProfiles);
      } else {
        setWorkspaceMembers([]);
      }

      // Show success message
      setSuccessMessage(`Member removed successfully!`);
      setShowSuccessNotification(true);
      setTimeout(() => setShowSuccessNotification(false), 3000);
    } catch (error) {
      console.error("Error removing member:", error);
    }
  };

  const handleTransferOwnership = async () => {
    if (!workspace?.id || !selectedNewOwner || !user?.id) return;

    setIsTransferring(true);
    try {
      // Change current owner to member
      await updateMemberRole(workspace.id, user.id, "member");

      // Change selected member to owner
      await updateMemberRole(workspace.id, selectedNewOwner.user_id, "owner");

      // Refresh workspace members with profiles
      const membersData = await getWorkspaceMembers(workspace.id);
      if (membersData && membersData.length > 0) {
        const membersWithProfiles = await Promise.all(
          membersData.map(async (member) => {
            try {
              const { data: profileData, error } = await supabase
                .from("profiles")
                .select("*")
                .eq("id", member.user_id)
                .single();

              if (!error && profileData) {
                return {
                  ...member,
                  ...profileData,
                  profile: profileData,
                };
              }
              return member;
            } catch (err) {
              console.error(
                "Error fetching profile for member:",
                member.user_id,
                err
              );
              return member;
            }
          })
        );
        setWorkspaceMembers(membersWithProfiles);
      }

      // Update local state
      setIsOwner(false);
      setUserRole("member");

      // Show success message
      setSuccessMessage(
        `Ownership transferred to ${getUserDisplayName(selectedNewOwner)} successfully!`
      );
      setShowSuccessNotification(true);
      setTimeout(() => setShowSuccessNotification(false), 5000);

      // Close dialog
      setShowTransferDialog(false);
      setSelectedNewOwner(null);
    } catch (error) {
      console.error("Error transferring ownership:", error);
    } finally {
      setIsTransferring(false);
    }
  };

  // ===== RETURN OBJECT =====

  return {
    // Form state
    editForm,
    setEditForm,
    workspaceSettings,
    setWorkspaceSettings,
    logoData,
    setLogoData,

    // UI state
    activeMainTab,
    setActiveMainTab,
    activeTeamTab,
    setActiveTeamTab,
    showSuccessNotification,
    setShowSuccessNotification,
    successMessage,
    setSuccessMessage,

    // Team management state
    searchQuery,
    setSearchQuery,
    isSearching,
    selectedUsers,
    setSelectedUsers,
    inviteMessage,
    setInviteMessage,
    isSending,
    members,
    pendingInvites,
    sentInvitations,
    recentlySentInvites,
    isLoadingStatus,
    searchResults,
    currentPage,
    hasMoreResults,
    loadingMoreUsers,
    realtimeChannel,
    resultsContainerRef,

    // Role management state
    workspaceMembers,
    isLoadingMembers,
    userRole,
    isOwner,

    // Transfer ownership state
    showTransferDialog,
    setShowTransferDialog,
    selectedNewOwner,
    setSelectedNewOwner,
    isTransferring,

    // Utility functions
    getInitials,
    getAvatarUrl,
    getUserDisplayName,
    getRoleBadge,
    refreshWorkspaceStatus,

    // Handler functions
    handleSettingChange,
    handleLogoChange,
    handleSubmit,
    handleDeleteWorkspace,
    handleUserSearch,
    handleSelectUser,
    handleRemoveUser,
    handleSendInvitations,
    handleRoleChange,
    handleRemoveMember,
    handleTransferOwnership,
  };
}

export default useWorkspaceSettings;
